/**
 * User-Defined Function (UDF) source safety validation (pentest APT-10).
 *
 * UDFs are user-authored JavaScript executed client-side. The platform intentionally
 * allows extending functionality, but there is no legitimate reason for a data-processing
 * UDF to reach for code-execution primitives (eval / Function constructor), dynamic module
 * loading, network exfiltration, or browser storage/credential access. This module rejects
 * those constructs before a UDF is executed, as a defence-in-depth control that also guards
 * against a future move of UDF evaluation server-side.
 *
 * This is a conservative source-level denylist (not a full AST allowlist) so it does not
 * break legitimate UDFs, which continue to use the ENDLA-provided utility functions exposed
 * on globalThis. Comments are stripped before scanning to avoid false positives from prose.
 */

interface UnsafePattern {
  readonly label: string;
  readonly pattern: RegExp;
}

const UNSAFE_PATTERNS: readonly UnsafePattern[] = [
  { label: "eval()", pattern: /\beval\s*\(/ },
  { label: "Function constructor", pattern: /\bnew\s+Function\b|\bFunction\s*\(/ },
  { label: "constructor escape", pattern: /\.constructor\s*\.\s*constructor\b/ },
  // `import` is a reserved word, so `x.import(...)` is always a method call, never a dynamic
  // import — graphology's `graph.import(...)` is bundled into most pipeline UDFs.
  { label: "dynamic import()", pattern: /(?<![.\w$])import\s*\(/ },
  { label: "importScripts()", pattern: /\bimportScripts\s*\(/ },
  { label: "require()", pattern: /\brequire\s*\(/ },
  { label: "child_process", pattern: /child_process/ },
  { label: "process access", pattern: /\bprocess\s*\.\s*(env|exit|binding|mainModule)\b/ },
  { label: "prototype pollution (__proto__)", pattern: /__proto__/ },
  { label: "document.cookie", pattern: /document\s*\.\s*cookie/ },
  { label: "network request (fetch)", pattern: /\bfetch\s*\(/ },
  { label: "network request (XMLHttpRequest)", pattern: /\bXMLHttpRequest\b/ },
  { label: "network request (WebSocket)", pattern: /\bWebSocket\b/ },
  { label: "network request (sendBeacon)", pattern: /\bsendBeacon\s*\(/ },
  { label: "browser storage (localStorage)", pattern: /\blocalStorage\b/ },
  { label: "browser storage (sessionStorage)", pattern: /\bsessionStorage\b/ },
  { label: "browser storage (indexedDB)", pattern: /\bindexedDB\b/ },
];

/** Remove line and block comments so dangerous words in prose don't trigger false positives. */
function stripComments(code: string): string {
  return code.replace(/\/\*[\s\S]*?\*\//g, " ").replace(/\/\/[^\n]*/g, " ");
}

/** Returns a list of human-readable unsafe constructs found in the UDF source (empty = safe). */
export function findUnsafeUdfConstructs(code: string): string[] {
  if (typeof code !== "string") {
    return [];
  }
  const scannable = stripComments(code);
  return UNSAFE_PATTERNS.filter(({ pattern }) => pattern.test(scannable)).map(({ label }) => label);
}

/** Throws a descriptive error if the UDF source contains disallowed constructs. */
export function assertSafeUserFunction(code: string): void {
  const violations = findUnsafeUdfConstructs(code);
  if (violations.length > 0) {
    throw new Error(
      `This function uses constructs that are not permitted for security reasons: ${violations.join(
        ", "
      )}. Remove them and try again.`
    );
  }
}
